import React, { useState, useEffect } from 'react';
import { ArrowLeft, Download, Eye, Send, Mail, CreditCard, Calendar, Hash, Building } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { useToast } from '../../../components/ui/use-toast';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [payment, setPayment] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [sendingConfirmation, setSendingConfirmation] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchPaymentDetails();
  }, [id]);

  const fetchPaymentDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      
      // Fetch payment
      const paymentResponse = await axios.get(`${API_URL}/api/finance/payments/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayment(paymentResponse.data);

      // Fetch related invoice
      if (paymentResponse.data.invoice_id) {
        try {
          const invoiceResponse = await axios.get(
            `${API_URL}/api/finance/invoices/${paymentResponse.data.invoice_id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setInvoice(invoiceResponse.data);
        } catch (error) {
          console.error('Error fetching invoice:', error);
        }
      }
    } catch (error) {
      console.error('Error fetching payment details:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load payment details. Please try again.",
      });
      navigate('/admin/finance/payments');
    } finally {
      setLoading(false);
    }
  };

  const handleSendConfirmation = async () => {
    setSendingConfirmation(true);
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${API_URL}/api/finance/payments/${payment.id}/send-confirmation`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast({
          title: "Success",
          description: "Payment confirmation sent successfully.",
          duration: 5000,
        });
        
        // Refresh payment to get updated receipt number
        await fetchPaymentDetails();
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.response?.data?.detail || "Failed to send confirmation.",
        duration: 5000,
      });
    } finally {
      setSendingConfirmation(false);
      setConfirmDialog(false);
    }
  };

  const handleDownloadReceipt = () => {
    const token = localStorage.getItem('admin_token');
    window.open(`${API_URL}/api/finance/receipts/${payment.receipt_number}/download?token=${token}`, '_blank');
  };

  const handlePreviewReceipt = () => {
    const token = localStorage.getItem('admin_token');
    window.open(`${API_URL}/api/finance/receipts/${payment.receipt_number}/preview?token=${token}`, '_blank');
  };

  const getStatusBadge = () => {
    const status = payment?.status || 'paid';
    const config = {
      paid: { label: 'Paid', color: 'bg-green-100 text-green-800 border-green-300' },
      pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
      failed: { label: 'Failed', color: 'bg-red-100 text-red-800 border-red-300' }
    };
    const { label, color } = config[status] || config.paid;
    return <Badge className={`${color} border font-medium`}>{label}</Badge>;
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00B4D8] mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading payment details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="p-6">
        <div className="text-center py-24">
          <CreditCard className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Payment not found</h3>
          <Button onClick={() => navigate('/admin/finance/payments')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Payments
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/admin/finance/payments')}
            className="hover:bg-gray-100"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Payment Details</h1>
            <p className="text-gray-600 mt-1">
              {payment.receipt_number ? `Receipt #${payment.receipt_number}` : 'Payment information'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {payment.receipt_number ? (
            <>
              <Button
                variant="outline"
                onClick={handlePreviewReceipt}
                className="border-[#00B4D8] text-[#00B4D8] hover:bg-blue-50"
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview Receipt
              </Button>
              <Button
                variant="outline"
                onClick={handleDownloadReceipt}
                className="border-[#023E8A] text-[#023E8A] hover:bg-blue-50"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Receipt
              </Button>
              <Button
                onClick={() => setConfirmDialog(true)}
                className="bg-[#00B4D8] hover:bg-[#0096c7] text-white"
              >
                <Send className="w-4 h-4 mr-2" />
                Resend Confirmation
              </Button>
            </>
          ) : (
            <Button
              onClick={() => setConfirmDialog(true)}
              className="bg-[#00B4D8] hover:bg-[#0096c7] text-white"
            >
              <Mail className="w-4 h-4 mr-2" />
              Send Confirmation
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Payment Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Payment Metadata */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Payment Information
                {getStatusBadge()}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                      <Hash className="w-4 h-4" />
                      <span>Payment ID</span>
                    </div>
                    <p className="text-base font-semibold text-gray-900">{payment.id}</p>
                  </div>
                  
                  <div>
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                      <Calendar className="w-4 h-4" />
                      <span>Payment Date</span>
                    </div>
                    <p className="text-base font-semibold text-gray-900">
                      {new Date(payment.payment_date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                      <CreditCard className="w-4 h-4" />
                      <span>Payment Method</span>
                    </div>
                    <p className="text-base font-semibold text-gray-900">{payment.payment_method}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Amount</div>
                    <p className="text-2xl font-bold text-[#023E8A]">
                      {payment.currency} {payment.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>

                  {payment.reference && (
                    <div>
                      <div className="text-sm text-gray-600 mb-1">Reference</div>
                      <p className="text-base font-semibold text-gray-900">{payment.reference}</p>
                    </div>
                  )}

                  {payment.notes && (
                    <div>
                      <div className="text-sm text-gray-600 mb-1">Notes</div>
                      <p className="text-sm text-gray-700">{payment.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Invoice Reference */}
          {invoice && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="w-5 h-5 text-[#00B4D8]" />
                  Related Invoice
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-6 border border-blue-100">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Invoice Number</p>
                      <p className="text-2xl font-bold text-[#023E8A]">#{invoice.invoice_number}</p>
                    </div>
                    <Badge className="bg-white text-[#023E8A] border border-[#023E8A]">
                      {invoice.status}
                    </Badge>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <p className="text-sm text-gray-600">Client</p>
                      <p className="font-semibold text-gray-900">{invoice.client_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Invoice Total</p>
                      <p className="font-semibold text-gray-900">
                        {invoice.currency} {invoice.total.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Issue Date</p>
                      <p className="font-semibold text-gray-900">
                        {new Date(invoice.issue_date).toLocaleDateString('en-US')}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Due Date</p>
                      <p className="font-semibold text-gray-900">
                        {new Date(invoice.due_date).toLocaleDateString('en-US')}
                      </p>
                    </div>
                  </div>

                  <Button
                    variant="outline"
                    className="w-full mt-4 border-[#023E8A] text-[#023E8A] hover:bg-blue-50"
                    onClick={() => navigate(`/admin/finance/invoices/${invoice.id}`)}
                  >
                    View Invoice Details
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Receipt Section */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Receipt</CardTitle>
              <CardDescription>
                {payment.receipt_number ? 'Payment receipt generated' : 'No receipt generated yet'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {payment.receipt_number ? (
                <div className="space-y-4">
                  <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-lg p-6 border border-cyan-200">
                    <div className="text-center mb-4">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white border-2 border-[#00B4D8] mb-3">
                        <Mail className="w-8 h-8 text-[#00B4D8]" />
                      </div>
                      <h3 className="font-semibold text-lg text-gray-900">Receipt Generated</h3>
                      <p className="text-sm text-gray-600 mt-1">#{payment.receipt_number}</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Button
                        variant="outline"
                        className="w-full border-[#00B4D8] text-[#00B4D8] hover:bg-blue-50"
                        onClick={handlePreviewReceipt}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Preview PDF
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full border-[#023E8A] text-[#023E8A] hover:bg-blue-50"
                        onClick={handleDownloadReceipt}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download PDF
                      </Button>
                    </div>
                  </div>

                  <div className="text-center">
                    <Button
                      onClick={() => setConfirmDialog(true)}
                      className="w-full bg-[#00B4D8] hover:bg-[#0096c7] text-white"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Resend Confirmation Email
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                    <Mail className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">No Receipt Yet</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Send a payment confirmation to generate and email the receipt
                  </p>
                  <Button
                    onClick={() => setConfirmDialog(true)}
                    className="w-full bg-[#00B4D8] hover:bg-[#0096c7] text-white"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Generate & Send Receipt
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Client Information */}
          {invoice && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Client Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm text-gray-600">Name</p>
                  <p className="font-semibold text-gray-900">{invoice.client_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="font-semibold text-gray-900">{invoice.client_email}</p>
                </div>
                {invoice.client_address && (
                  <div>
                    <p className="text-sm text-gray-600">Address</p>
                    <p className="text-sm text-gray-700 whitespace-pre-line">{invoice.client_address}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog} onOpenChange={setConfirmDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#023E8A] text-xl font-semibold">
              Send Payment Confirmation
            </DialogTitle>
            <DialogDescription className="text-gray-600 text-base leading-relaxed">
              This will send a confirmation email with the receipt PDF to the client.
              {payment.receipt_number && (
                <span className="block mt-2 text-sm text-[#00B4D8]">
                  Receipt #{payment.receipt_number} will be attached.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="bg-gray-50 rounded-lg p-4 space-y-2 my-4">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Amount:</span>
              <span className="font-semibold text-gray-900">
                {payment.currency} {payment.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Method:</span>
              <span className="font-semibold text-gray-900">{payment.payment_method}</span>
            </div>
            {invoice && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Client:</span>
                <span className="font-semibold text-gray-900">{invoice.client_email}</span>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setConfirmDialog(false)}
              disabled={sendingConfirmation}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendConfirmation}
              disabled={sendingConfirmation}
              className="bg-[#00B4D8] hover:bg-[#0096c7] text-white"
            >
              {sendingConfirmation ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentDetails;
